import {useLocation, useNavigate, useParams} from "react-router-dom";
import {Badge, Button, Checkbox, Col, Input, message, Row, Space, Table, TableProps, Spin} from "antd";
import React, {useEffect, useState,useRef} from "react";
import {UtilsService} from "../../../services/Utils.service";
import {ArrowRightOutlined, CheckOutlined, DownloadOutlined, MinusOutlined, SearchOutlined} from "@ant-design/icons";
import {HttpService} from "../../../services/http/Http.service";
import DetailModal from "../workOnPractices/DetailModal";
import {PracticeExportService} from "../PracticeExport.service";
import Title from "antd/es/typography/Title";
import {SelectAllButton} from "../SelectAllButton";
import ConfirmModal from "../../../components/confirmModal/ConfirmModal";
import {RoutingURL, SimulatedFileTS, StatusCodeE} from "../../../model/models";
import {combinatorColumns} from "../combinatorColumns";
import {useProgressContext} from "../../../services/ProgressContext";
import {usePaginatedData} from "../../../services/hooks/usePaginatedData";
import {HttpErrorService} from "../../../services/http/HttpError.service";
import {AuthService} from "../../../services/Auth.service";


export default function InProgressPractices() {
    const navigate = useNavigate();
    const {id} = useParams();
    const location = useLocation();
    const {startPolling} = useProgressContext();
    const [filterHasPagoPaOnly, setFilterHasPagoPaOnly] = useState(false);
    const [filterLoading, setFilterLoading] = useState(false);
    const {
        data: filteredData,
        totalItems,
        pageData,
        searchText,
        setSearchText,
        handleTableChange,
        reloadCurrentPage,
    } = usePaginatedData<SimulatedFileTS>({
        simulatorId: id,
        status: StatusCodeE.IN_LAVORAZIONE,
        initialPageSize: 20,
        filterHasPagoPaOnly: filterHasPagoPaOnly,
    });

    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
    const [combinatorData, setCombinatorData] = useState(location.state?.simulatorData);
    const [selectedTotal, setSelectedTotal] = useState(0);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
    const [isOpen, setIsOpen] = useState(false)
    const [userId, setUserId] = useState<number>()
    const [username, setUsername] = useState<string>("")
    const [fiscalCode, setFiscalCode] = useState<string>("")
    const [type, setType] = useState<string>("")
    const [isRateApplied, setIsRateApplied] = useState<boolean>(false)
    const [uuid, setUuid] = useState<string | null>(null);
    let [allRowsHavePagoPa, setAllRowsHavePagoPa] = useState<boolean>(false)
    let [atleastARowHasPagoPa, setAtleastARowHasPagoPa] = useState<boolean>(false)
    const [resetSelectAll, setResetSelectAll] = useState(false);
    const skipInitialPagoPaFilterChange = useRef(true);

    useEffect(() => {
        if (!combinatorData) {
            HttpService.getInstanceAxios().get(`/simulazione/${id}/statistiche`)
                .then(response => {
                    const item = response.data || {};

                    const {simulator, statistics} = item;
                    const {filters} = simulator;

                    let yearLabel = filters.years.join(" - ");
                    if (filters.checked) {
                        yearLabel += " (Anni indicati)";
                    }

                    let exemptionLabel = "Tutti";
                    if (filters.exemptionCodes?.length > 0) {
                        exemptionLabel = filters.exemptionCodes.join(" - ");
                    }
                    if (filters.exemptionCodeChecked) {
                        exemptionLabel += " (Codici indicati)";
                    }
                    setCombinatorData([{
                        key: simulator.id,
                        numberPositions: statistics.totalOccurrences,
                        numberAssisted: statistics.totalPerson,
                        exemptionCode: exemptionLabel,
                        year: yearLabel,
                        type: statistics.typeList?.join(" - "),
                        amount: "€ " + UtilsService.formatAmountRange([filters.minAmount, filters.maxAmount]),
                        ticket: "€ " + UtilsService.formatNumber(statistics.totalTicket),
                    }]);
                })
        }
    }, [combinatorData]);

    useEffect(() => {
        if (userId !== null && userId !== undefined) {
            setIsOpen(true);
        }
    }, [userId]);

    useEffect(() => {
        if (skipInitialPagoPaFilterChange.current) {
            skipInitialPagoPaFilterChange.current = false;
            return;
        }

        const fetchData = async () => {
            setFilterLoading(true);
            await reloadCurrentPage();
            setFilterLoading(false);
        };
        fetchData();
    }, [filterHasPagoPaOnly]);

    const rowSelection: TableProps<SimulatedFileTS>['rowSelection'] = {
        selectedRowKeys: selectedRowKeys,
        onChange: (selectedRowKeys: React.Key[], selectedRows: SimulatedFileTS[]) => {
            const total = selectedRows.reduce((sum, row) => sum + row.totalSum, 0);
            setSelectedTotal(total);
            setSelectedRowKeys(selectedRowKeys)
            setAllRowsHavePagoPa(selectedRows.length > 0 && selectedRows.every(row => row.hasPayment));
            setAtleastARowHasPagoPa(selectedRows.some(row => row.hasPayment));
        },

    };

    const applyPayments = async () => {
        if (selectedRowKeys.length > 0) {
            if (!atleastARowHasPagoPa) {
                try {
                    const data = await HttpService.getInstanceAxios().post(`/payment/${id}/apply-payments`, selectedRowKeys)
                    setSelectedRowKeys([])
                    setSelectedTotal(0)
                    setUuid(data.data);
                    startPolling(data.data, () => {
                        reloadCurrentPage()
                    });

                    setResetSelectAll(true);
                    setTimeout(() => setResetSelectAll(false), 0);
                } catch (error) {
                    HttpErrorService.handle("Errore durante la generazione dei PagoPa", error, true)
                }
            } else message.error("E' già stato generato il PagoPA di una o più pratiche selezionate!")
        } else message.error("Nessun utente selezionato.")
        setIsPaymentModalOpen(false)
    }

    async function exportToExcel(xlsx: string) {
        if (!id) return;
        setIsExporting(true);
        try {
            await PracticeExportService.loadAndExportToExcel(Number.parseInt(id), StatusCodeE.IN_LAVORAZIONE, xlsx);
        } finally {
            setIsExporting(false);
        }
    }

    const handleModal = (record: SimulatedFileTS) => {
        setUserId(record.personId)
        setUsername(record.fullName)
        setFiscalCode(record.fiscalCode)
        setType(record.type)
        setIsRateApplied(record.hasInstallment)
    }

    const [isExporting, setIsExporting] = useState(false);
    const [onNextLoading, setonNextLoading] = useState(false);

    const onNext = async () => {
        if (selectedRowKeys.length > 0) {
            if (allRowsHavePagoPa) {
                try {
                    setonNextLoading(true);
                    const data = await HttpService.getInstanceAxios().post(`/file-ts/${id}/next-state/async`, selectedRowKeys)
                    setSelectedRowKeys([])
                    setSelectedTotal(0)
                    setUuid(data.data);
                    startPolling(data.data, async () => {
                        setonNextLoading(false);
                        if (AuthService.isAdmin()) {
                            await reloadCurrentPage()
                        } else {
                            let url: string = UtilsService.replaceId(RoutingURL.PRACTICES, id);
                            url = UtilsService.replaceStatus(url, StatusCodeE.DA_FIRMARE);
                            navigate(url);
                        }
                    }, async (progressData) => {
                        setonNextLoading(false);
                        message.error(progressData?.errorMessage
                            ? `Errore durante il passaggio di stato: ${progressData.errorMessage}`
                            : "Errore durante il passaggio di stato");
                        await reloadCurrentPage()
                    });

                    setResetSelectAll(true);
                    setTimeout(() => setResetSelectAll(false), 0);
                } catch (error) {
                    setonNextLoading(false);
                    HttpErrorService.handle("Errore durante il passaggio di stato", error, true)
                }
            } else message.error("Non è stato generato il PagoPA di una o più pratiche selezionate!")
        } else message.error("Nessun utente selezionato.")
    }

    const [onBackLoading, setOnBackLoading] = useState(false);

    const onBack = async () => {
        setOnBackLoading(true);
        if (selectedRowKeys.length > 0) {
            try {
                await HttpService.getInstanceAxios().post(`/file-ts/${id}/previous-state`, selectedRowKeys)
                let url: string = UtilsService.replaceId(RoutingURL.PRACTICES, id);
                url = UtilsService.replaceStatus(url, StatusCodeE.DA_LAVORARE);
                navigate(url);
            } catch (error) {
                HttpErrorService.handle("Errore durante la rielaborazione delle pratiche", error, true)
            } finally {
                setOnBackLoading(false);
            }
        } else message.error("Nessun utente selezionato.")
    }

    const columns = [
        {
            title: "Codice Fiscale",
            dataIndex: "fiscalCode",
            key: "fiscalCode",
            sorter: true, // let backend handle sorting
        },
        {
            title: "Cognome Nome",
            dataIndex: "fullName",
            key: "fullName",
            sorter: true, // let backend handle sorting
        },
        {
            title: "Anno",
            dataIndex: "years",
            key: "years",
            render: (years: string[]) => years.sort((a, b) => parseInt(a) - parseInt(b)).join(" - ")
        },
        {
            title: "Codici esenzione",
            dataIndex: "exemptionCodes",
            key: "exemptionCodes",
            sorter: true,
            render: (codes: string[]) => codes.join(" - ")
        },
        {
            title: "Tipo",
            dataIndex: "type",
            key: "type"
        },
        {
            title: "Rate",
            dataIndex: "hasInstallment",
            key: "hasInstallment",
            render: (value: boolean) => (value ? <CheckOutlined style={{color: 'var(--main-color)'}}/> :
                <MinusOutlined/>),
        },
        {
            title: "PagoPA",
            dataIndex: "hasPayment",
            key: "hasPayment",
            render: (value: boolean) => (value ? <CheckOutlined style={{color: 'var(--main-color)'}}/> :
                <MinusOutlined/>),
        },
        {
            title: "Interessi",
            dataIndex: "totalInterest",
            key: "totalInterest",
            render: (value: number) => (value > 0 ? <CheckOutlined style={{color: 'var(--main-color)'}}/> :
                <MinusOutlined/>),
        },
        {
            title: "Importo totale",
            dataIndex: "totalSum",
            key: "totalSum",
            sorter: true, // let backend handle it now
            render: (value: number) => "€ " + UtilsService.formatNumber(value)
        },
        {
            title: "Azioni",
            key: "operation",
            render: (record: SimulatedFileTS) => (
                <Space>
                    <Button onClick={() => handleModal(record)} type="primary" icon={<ArrowRightOutlined/>}/>
                </Space>
            )
        },
    ];

    return (
        <>
            <Row justify="space-between" align="middle">
                <Col>
                    <Input
                        style={{width: "400px"}}
                        size="middle"
                        allowClear
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        addonBefore={<SearchOutlined/>}
                        placeholder="Nome, Cognome, Codice Fiscale"
                    />
                </Col>
                <Col>
                    <Space>
                        <Spin spinning={filterLoading} size="small">
                        <Checkbox
                            checked={filterHasPagoPaOnly}
                            onChange={(e) => setFilterHasPagoPaOnly(e.target.checked)}
                            className={`solo-pagopa ${filterHasPagoPaOnly ? 'checked' : ''}`}
                            disabled={filterLoading}
                        >
                            Solo con PagoPA
                        </Checkbox>
                        </Spin>

                        <Button onClick={() => setIsPaymentModalOpen(true)} type={"dashed"}>Genera PagoPA</Button>
                        <Button
                            className="custom-label-color"
                            type="primary"
                            icon={<DownloadOutlined/>}
                            loading={isExporting}
                            onClick={() => exportToExcel("Report.xlsx")}
                        >
                            Esporta
                        </Button>
                    </Space>
                </Col>
            </Row>
            <Space size={30} direction={"vertical"} style={{width: "100%"}}>
                <Table
                    columns={combinatorColumns}
                    dataSource={combinatorData}
                    pagination={false}
                    bordered
                />
                <Space style={{display: "flex", justifyContent: "space-between"}}>
                    {id && <SelectAllButton
                        idSimulator={id}
                        status={StatusCodeE.IN_LAVORAZIONE}
                        onSelected={(keys) => {
                            setSelectedRowKeys(keys);

                            const keyNumbers = keys.map(k => Number(k));
                            const selectedRows = filteredData.filter(row => keyNumbers.includes(row.personId));

                            const total = selectedRows.reduce((sum, row) => sum + (row.totalSum || 0), 0);
                            setSelectedTotal(total);

                            const allHavePagoPa = selectedRows.length > 0 && selectedRows.every(row => row.hasPayment);
                            setAllRowsHavePagoPa(allHavePagoPa);
                        }}
                        resetSelectAll={resetSelectAll}

                    />

                    }
                    <div>
                        Utenti selezionati: <Badge color={"#007A33FF"}
                                                   count={selectedRowKeys ? selectedRowKeys.length : 0} showZero={true}
                                                   overflowCount={9999}
                                                   style={{fontSize: "14px", transform: "scale(1.1)"}}/>
                    </div>
                </Space>
                <Table<SimulatedFileTS>
                    rowSelection={{type: "checkbox", ...rowSelection}}
                    rowKey={record => record.personId}
                    rowClassName={record => {
                        return record.status === StatusCodeE.ATTESA_PAGOPA ? 'disabled-row' : '';
                    }}
                    dataSource={filteredData}
                    columns={columns}
                    loading={filterLoading}
                    pagination={{
                        total: totalItems,
                        showTotal: (total) => `Totale ${total} elementi`,
                        current: pageData.current,
                        pageSize: pageData.pageSize,
                        pageSizeOptions: ["10", "20", "50", "100", "250", "500"],
                        showSizeChanger: true,
                    }}
                    onChange={handleTableChange}
                />
            </Space>
            <Space className={"custom-space"}>
                <Button onClick={onBack} size={"large"} type={"dashed"} loading={onBackLoading} danger>Rielabora
                    pratiche</Button>
                <Button className="custom-label-color" onClick={onNext} size={"large"}
                        type={"primary"} loading={onNextLoading}>Continua</Button>
            </Space>
            <Title className={"custom-title"} level={5}>Importo selezionato:
                € {UtilsService.formatNumber(selectedTotal)}</Title>
            {isOpen ?
                <DetailModal isOpen={isOpen}
                             combinatorId={id}
                             setIsOpen={setIsOpen}
                             userId={userId}
                             setUserId={setUserId}
                             username={username}
                             fiscalCode={fiscalCode}
                             type={type}
                             isRateApplied={isRateApplied}
                />
                : null
            }
            <ConfirmModal
                visible={isPaymentModalOpen}
                onConfirm={applyPayments}
                onCancel={() => setIsPaymentModalOpen(false)}
                title="Genera PagoPA"
                message="Sei sicuro di voler generare i PagoPA?"
                confirmText="Sì, genera"
                cancelText="Annulla"
            />
        </>
    )
}
